import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import * as XLSX from 'xlsx-js-style';
import { API_URL, getAuthHeaders, formatDate, formatDateTime, formatTime, cn } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { 
  DollarSign, 
  Clock, 
  ClipboardCheck, 
  User, 
  FileText, 
  Printer,
  Calendar,
  Coffee,
  CheckCircle2,
  XCircle,
  History,
  Download,
  MapPin
} from "lucide-react";
import { toast } from "sonner";
import LocationMapDialog from "../components/Map/LocationMapDialog";

export const Payroll = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  // Modal de mapa para la ubicación de un punch del volante de pago
  const [mapLocation, setMapLocation] = useState(null);
  const [mapTitle, setMapTitle] = useState("");
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const openPunchMap = (record) => {
    if (!Number.isFinite(Number(record?.latitude)) || !Number.isFinite(Number(record?.longitude))) return;
    setMapLocation({ latitude: Number(record.latitude), longitude: Number(record.longitude) });
    setMapTitle(`${getPunchTypeLabel(record.punch_type)} · ${formatDateTime(record.timestamp)}`);
    setIsMapDialogOpen(true);
  };
  const { can, canManageCompanies } = useAuth();
  const [payrollData, setPayrollData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]); // Todos los usuarios (para buscar paid_by)
  const [vigilantes, setVigilantes] = useState([]); // Solo vigilantes (para el filtro)
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [payrollDetail, setPayrollDetail] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isPaystubOpen, setIsPaystubOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedPaymentItem, setSelectedPaymentItem] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentPeriod, setPaymentPeriod] = useState({ start: "", end: "" });
  const [isAdvancePayment, setIsAdvancePayment] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyFilterUser, setHistoryFilterUser] = useState("all");
  const printRef = useRef(null);
  const printIframeRef = useRef(null);

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany && selectedCompany !== "all") {
        params.append("company_id", selectedCompany);
      }
      if (selectedUser && selectedUser !== "all") {
        params.append("user_id", selectedUser);
      }
      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }

      const response = await axios.get(
        `${API_URL}/payroll/summary?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      const data = Array.isArray(response.data) ? response.data : [];
      // Debug: Log para verificar datos de pagos parciales
      data.forEach(item => {
        const totalToPay = item.total_payment_after_advance || item.total_payment || 0;
        const totalPaid = item.total_paid || 0;
        const hasPartial = totalPaid > 0 && totalPaid < totalToPay && !item.is_paid;
        
        if (item.total_paid > 0 || item.is_partial || hasPartial) {
          console.log("Payroll item with payment:", {
            user: item.user_name,
            total_payment: item.total_payment,
            total_payment_after_advance: item.total_payment_after_advance,
            total_paid: item.total_paid,
            is_partial: item.is_partial,
            is_paid: item.is_paid,
            hasPartial: hasPartial,
            shouldShowStrikethrough: hasPartial || item.is_partial
          });
        }
      });
      setPayrollData(data);
    } catch (error) {
      console.error("Error fetching payroll:", error);
      setPayrollData([]);
      toast.error(getErrorMessage(error, "api.errors.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      const [companiesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/users`, { headers: getAuthHeaders() }),
      ]);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
      // Guardar todos los usuarios para poder mostrar "Registrado por" correctamente
      setUsers(usersData);
      // Guardar solo vigilantes para el filtro
      setVigilantes(usersData.filter((u) => u.role === "vigilante"));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchPayroll();
    // Cargar historial de pagos en segundo plano para calcular días pagados (sin filtro de usuario)
    fetchPaymentHistory(null, true);
  }, [selectedCompany, selectedUser, startDate, endDate]);

  useEffect(() => {
    // Si el historial está visible, recargarlo con el filtro de usuario correspondiente
    if (showHistory) {
      fetchPaymentHistory(null, false);
    }
  }, [showHistory, historyFilterUser]);

  const viewPayrollDetail = async (userId) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await axios.get(
        `${API_URL}/payroll/detail/${userId}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setPayrollDetail(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error("Error al cargar detalle");
    }
  };

  const openPaystub = async (userId) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await axios.get(
        `${API_URL}/payroll/detail/${userId}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setPayrollDetail(response.data);
      setIsPaystubOpen(true);
    } catch (error) {
      toast.error("Error al generar volante");
    }
  };

  const openPaymentDialog = async (item, isAdvance = false) => {
    setSelectedPaymentItem(item);
    setPaymentNotes("");
    setIsAdvancePayment(isAdvance); // Establecer según el parámetro
    
    // Si es pago por adelantado, permitir cualquier monto
    if (isAdvance) {
      setPaymentAmount(0);
    } else {
      // Si hay pagos parciales, establecer el monto inicial como el saldo pendiente
      // Usar total_payment_after_advance si existe, sino usar total_payment
      const totalToPay = item.total_payment_after_advance || item.total_payment;
      const remaining = totalToPay - (item.total_paid || 0);
      setPaymentAmount(remaining > 0 ? remaining : totalToPay);
    }
    
    // Para pagos por adelantado, no necesitamos el período del detalle
    // El usuario puede establecer cualquier período futuro
    if (isAdvance) {
      // Establecer un período por defecto (próximas 2 semanas) o usar filtros si existen
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 14);
      
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      setPaymentPeriod({
        start: startDate || formatDate(today),
        end: endDate || formatDate(nextWeek)
      });
    } else {
      // Obtener el período del detalle si no hay filtros de fecha
      if (!startDate || !endDate) {
        try {
          const params = new URLSearchParams();
          if (startDate) params.append("start_date", startDate);
          if (endDate) params.append("end_date", endDate);
          
          const response = await axios.get(
            `${API_URL}/payroll/detail/${item.user_id}?${params.toString()}`,
            { headers: getAuthHeaders() }
          );
          
          if (response.data.period_start && response.data.period_end) {
            setPaymentPeriod({
              start: response.data.period_start,
              end: response.data.period_end
            });
          } else {
            setPaymentPeriod({ start: startDate || "", end: endDate || "" });
          }
        } catch (error) {
          console.error("Error al obtener detalle:", error);
          setPaymentPeriod({ start: startDate || "", end: endDate || "" });
        }
      } else {
        setPaymentPeriod({ start: startDate, end: endDate });
      }
    }
    
    setIsPaymentDialogOpen(true);
  };

  const markAsPaid = async () => {
    if (!selectedPaymentItem) return;
    
    try {
      const periodStart = paymentPeriod.start || startDate || "";
      const periodEnd = paymentPeriod.end || endDate || "";
      
      if (!periodStart || !periodEnd) {
        toast.error("Debe seleccionar un rango de fechas para registrar el pago");
        return;
      }
      
      if (paymentAmount <= 0) {
        toast.error("El monto debe ser mayor a cero");
        return;
      }
      
      // Validar que no se pague más de lo pendiente (solo para pagos regulares)
      if (!isAdvancePayment) {
        const totalToPay = selectedPaymentItem.total_payment_after_advance || selectedPaymentItem.total_payment || 0;
        const totalPaid = selectedPaymentItem.total_paid || 0;
        const remainingBalance = totalToPay - totalPaid;
        
        if (paymentAmount > remainingBalance) {
          toast.error(`No se puede pagar más de $${remainingBalance.toFixed(2)}. El saldo pendiente es de $${remainingBalance.toFixed(2)}.`);
          return;
        }
      }
      
      await axios.post(
        `${API_URL}/payroll/payments`,
        {
          user_id: selectedPaymentItem.user_id,
          period_start: periodStart,
          period_end: periodEnd,
          total_payment: parseFloat(paymentAmount),
          notes: paymentNotes || undefined,
          is_advance: isAdvancePayment
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(isAdvancePayment ? "Pago por adelantado registrado correctamente" : "Pago registrado correctamente");
      setIsPaymentDialogOpen(false);
      setSelectedPaymentItem(null);
      setPaymentNotes("");
      setPaymentAmount(0);
      setPaymentPeriod({ start: "", end: "" });
      setIsAdvancePayment(false);
      
      // Refrescar datos: siempre recargar payroll y historial de pagos
      // Primero recargar el historial sin filtro para que los cálculos sean correctos
      await fetchPaymentHistory(null, true);
      
      // Luego recargar el payroll para que muestre los totales actualizados
      await fetchPayroll();
      
      // Si el historial está visible, recargarlo con el filtro correspondiente
      if (showHistory) {
        fetchPaymentHistory(null, false);
      }
    } catch (error) {
      console.error("Error al registrar pago:", error);
      toast.error(getErrorMessage(error, "api.errors.registerPaymentError"));
    }
  };

  const fetchPaymentHistory = async (userId = null, skipUserFilter = false) => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany && selectedCompany !== "all") {
        params.append("company_id", selectedCompany);
      }
      // Si skipUserFilter es true, no aplicar filtro de usuario (para cálculos)
      // Usar userId pasado como parámetro, o el filtro del historial, o el filtro general
      if (!skipUserFilter) {
      const userToFilter = userId || (historyFilterUser !== "all" ? historyFilterUser : (selectedUser && selectedUser !== "all" ? selectedUser : null));
      if (userToFilter) {
        params.append("user_id", userToFilter);
        }
      }
      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }

      const response = await axios.get(
        `${API_URL}/payroll/payments?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setPaymentHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Error al cargar historial de pagos");
      setPaymentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const viewUserPaymentHistory = (userId) => {
    setHistoryFilterUser(userId);
    setShowHistory(true);
    fetchPaymentHistory(userId);
  };

  // Calcular días pagados para un usuario específico en el período actual
  const getDaysPaidForUser = (userId) => {
    if (!Array.isArray(paymentHistory) || paymentHistory.length === 0) {
      return 0;
    }
    
    // Filtrar pagos del usuario que no sean adelantos y que estén en el período actual
    const userPayments = paymentHistory.filter(payment => {
      if (payment.user_id !== userId || payment.is_advance) {
        return false;
      }
      
      // Si hay filtros de fecha, verificar que el pago esté en el rango
      if (startDate || endDate) {
        const paymentStart = payment.period_start;
        const paymentEnd = payment.period_end;
        
        if (startDate && paymentEnd < startDate) return false;
        if (endDate && paymentStart > endDate) return false;
      }
      
      return true;
    });
    
    // Sumar los días trabajados de todos los pagos
    return userPayments.reduce((sum, payment) => sum + (payment.days_worked || 0), 0);
  };

  // Calcular monto total pagado para un usuario específico en el período actual
  const getAmountPaidForUser = (userId, periodStart, periodEnd) => {
    if (!Array.isArray(paymentHistory) || paymentHistory.length === 0) {
      return 0;
    }
    
    // Filtrar pagos del usuario que no sean adelantos y que se solapen con el período
    const userPayments = paymentHistory.filter(payment => {
      if (payment.user_id !== userId || payment.is_advance) {
        return false;
      }
      
      // Verificar que el pago se solape con el período
      if (periodStart && periodEnd) {
        const paymentStart = payment.period_start;
        const paymentEnd = payment.period_end;
        
        // Verificar solapamiento
        if (paymentEnd < periodStart || paymentStart > periodEnd) {
          return false;
        }
      } else if (startDate || endDate) {
        // Si no hay período específico pero hay filtros de fecha, usar esos
        const paymentStart = payment.period_start;
        const paymentEnd = payment.period_end;
        
        if (startDate && paymentEnd < startDate) return false;
        if (endDate && paymentStart > endDate) return false;
      }
      
      return true;
    });
    
    // Sumar los montos de todos los pagos
    return userPayments.reduce((sum, payment) => sum + (payment.total_payment || 0), 0);
  };

  const exportToExcel = async () => {
    try {
      // Obtener detalles de nómina para cada usuario
      const exportData = [];
      const companyName = companies.find(c => c.id === selectedCompany)?.name || t("pages.routes.allCompanies");
      
      // Construir texto del período basado en las fechas seleccionadas
      let periodText = "";
      if (startDate && endDate) {
        periodText = t("pages.payroll.periodFrom", { start: startDate, end: endDate });
      } else if (startDate) {
        periodText = t("pages.payroll.periodSince", { start: startDate });
      } else if (endDate) {
        periodText = t("pages.payroll.periodUntil", { end: endDate });
      } else {
        periodText = t("pages.payroll.fullPeriod");
      }
      
      for (const item of filteredPayrollData) {
        // Obtener días trabajados con detalles
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        
        const response = await axios.get(
          `${API_URL}/payroll/detail/${item.user_id}?${params.toString()}`,
          { headers: getAuthHeaders() }
        );
        
        const detail = response.data;
        
        // Agregar fila principal del empleado
        exportData.push({
          nombre: item.user_name,
          cedula: item.email,
          fecha_entrada: detail.period_start || "",
          dias_trabajados: item.days_worked,
          dias_feriado: 0,
          horas_trabajadas: item.total_hours.toFixed(2),
          horas_extras: 0,
          incentivos: 0,
          anticipos: item.advance_debt || 0,
          total: (item.total_payment_after_advance || item.total_payment).toFixed(2),
        });
        
        // Agregar detalles de días trabajados
        if (detail.workdays && detail.workdays.length > 0) {
          detail.workdays.forEach(day => {
            exportData.push({
              nombre: "",
              cedula: "",
              fecha: day.date,
              entrada: day.clock_in ? new Date(day.clock_in).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : "",
              salida: day.clock_out ? new Date(day.clock_out).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : "",
              horas: day.total_hours?.toFixed(2) || "0.00",
            });
          });
        }
      }
      
      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      
      // Hoja 1: Resumen
      const wsData = [
        [t("pages.payroll.reportTitle")],
        [companyName.toUpperCase()],
        [periodText.toUpperCase()],
        [],
        [t("pages.payroll.columnNames"), t("pages.payroll.columnId"), t("pages.payroll.columnEntryDate"), t("pages.payroll.columnWorkedDay"), t("pages.payroll.columnDoubleWorkedDay"), t("pages.payroll.columnFreeDay"), t("pages.payroll.columnAbsences"), t("pages.payroll.columnOvertime"), t("pages.payroll.columnIncentives"), t("pages.payroll.columnAdvances"), t("pages.payroll.columnTotalDaysWorked"), t("pages.payroll.columnTotal")],
      ];
      
      filteredPayrollData.forEach(item => {
        // Los días feriados trabajados son ADICIONALES, no se restan del total
        const holidayDays = item.holiday_days || 0;
        const totalDaysWorked = item.days_worked || 0;
        
        wsData.push([
          item.user_name,
          item.email,
          item.created_at || "",  // Fecha de entrada (creación del usuario)
          totalDaysWorked, // Total días trabajados (incluye normales y feriados)
          holidayDays, // Días trabajados en feriado (para pagar extra)
          0, // Días libre (por implementar)
          item.absences || 0, // Faltas
          (item.total_overtime_hours || 0).toFixed(2), // Horas extras totales con 2 decimales
          0, // Incentivos (por implementar)
          (item.advance_debt || 0).toFixed(2), // Anticipos con 2 decimales
          totalDaysWorked, // Total días trabajados
          (item.total_payment_after_advance || item.total_payment || 0).toFixed(2), // Total con 2 decimales
        ]);
      });
      
      // Agregar totales
      wsData.push([]);
      
      // Calcular totales
      const totalDaysWorked = filteredPayrollData.reduce((sum, p) => sum + (p.days_worked || 0), 0);
      const totalHolidayDays = filteredPayrollData.reduce((sum, p) => sum + (p.holiday_days || 0), 0);
      const totalAbsences = filteredPayrollData.reduce((sum, p) => sum + (p.absences || 0), 0);
      const totalOvertimeHours = filteredPayrollData.reduce((sum, p) => sum + (p.total_overtime_hours || 0), 0);
      const totalAdvances = filteredPayrollData.reduce((sum, p) => sum + (p.advance_debt || 0), 0);
      
      wsData.push([
        t("pages.payroll.totals"),
        "",
        "",
        totalDaysWorked, // Total días trabajados (sin restar feriados)
        totalHolidayDays, // Total días feriados trabajados
        0,
        totalAbsences,
        totalOvertimeHours.toFixed(2),
        0,
        totalAdvances.toFixed(2),
        totalDaysWorked, // Total días trabajados
        totalPayroll.toFixed(2),
      ]);
      
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Ajustar anchos de columna
      ws['!cols'] = [
        { wch: 30 }, // Nombre
        { wch: 15 }, // Cédula
        { wch: 15 }, // Fecha entrada
        { wch: 15 }, // Día trabajado
        { wch: 18 }, // Día trabajado doble
        { wch: 12 }, // Día libre
        { wch: 10 }, // Faltas
        { wch: 12 }, // Horas extras
        { wch: 12 }, // Incentivos
        { wch: 12 }, // Anticipos
        { wch: 18 }, // Total días trabajados
        { wch: 12 }, // Total
      ];
      
      // Aplicar estilos a las celdas
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Estilos para el título principal (fila 1) - Fondo negro, texto blanco
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + "1";
        if (!ws[address]) ws[address] = { t: 's', v: '' };
        ws[address].s = {
          font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }
      
      // Estilos para compañía (fila 2) - Fondo negro, texto blanco
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + "2";
        if (!ws[address]) ws[address] = { t: 's', v: '' };
        ws[address].s = {
          font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }
      
      // Estilos para período (fila 3) - Fondo negro, texto blanco
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + "3";
        if (!ws[address]) ws[address] = { t: 's', v: '' };
        ws[address].s = {
          font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }
      
      // Estilos para encabezados de columnas (fila 5) - Fondo negro, texto blanco
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + "5";
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: {
            top: { style: "thin", color: { rgb: "FFFFFF" } },
            bottom: { style: "thin", color: { rgb: "FFFFFF" } },
            left: { style: "thin", color: { rgb: "FFFFFF" } },
            right: { style: "thin", color: { rgb: "FFFFFF" } }
          }
        };
      }
      
      // Estilos para la fila de totales (última fila) - Fondo gris oscuro
      const lastRow = range.e.r + 1;
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + lastRow;
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true, sz: 10, color: { rgb: "000000" } },
          fill: { fgColor: { rgb: "D9D9D9" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }
      
      // Agregar bordes y fondo a las celdas de datos
      for (let R = 5; R < lastRow - 1; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[address]) continue;
          
          // Alternar colores de fila (blanco y gris muy claro)
          const fillColor = (R % 2 === 0) ? "FFFFFF" : "F2F2F2";
          
          ws[address].s = {
            fill: { fgColor: { rgb: fillColor } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "D3D3D3" } },
              bottom: { style: "thin", color: { rgb: "D3D3D3" } },
              left: { style: "thin", color: { rgb: "D3D3D3" } },
              right: { style: "thin", color: { rgb: "D3D3D3" } }
            }
          };
        }
      }
      
      // Merge cells para el título (fila 1)
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 11 } }, // Título
        { s: { r: 1, c: 0 }, e: { r: 1, c: 11 } }, // Compañía
        { s: { r: 2, c: 0 }, e: { r: 2, c: 11 } }, // Período
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, "Resumen");
      
      // Generar archivo
      const fileName = `Nomina_${companyName.replace(/\s+/g, '_')}_${startDate || 'completo'}_${endDate || ''}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      toast.success(t("pages.payroll.exportSuccess"));
    } catch (error) {
      console.error("Error al exportar:", error);
      toast.error(t("pages.payroll.exportError"));
    }
  };

  const handlePrint = () => {
    if (!printIframeRef.current) {
      toast.error(getErrorMessage(error, "api.errors.printError"));
      return;
    }

    const iframe = printIframeRef.current;
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    
    // Obtener traducciones antes de construir el string
    const paystubTitle = t("pages.payroll.paystubTitle");
    const employeeInfo = t("pages.payroll.employeeInfo");
    const paymentPeriod = t("pages.payroll.paymentPeriod");
    const hoursSummary = t("pages.payroll.hoursSummary");
    const punchHistory = t("pages.payroll.punchHistory");
    const totalHours = t("pages.payroll.totalHours");
    const totalToPay = t("pages.payroll.totalToPayLabel");
    const fixedSalary = t("pages.payroll.fixedSalary");
    const noRecords = t("pages.payroll.noRecords");
    const totalLunch = t("pages.payroll.totalLunch");
    const dateLabel = t("common.date");
    const clockIn = t("pages.payroll.punchTypeClockIn");
    const clockOut = t("pages.payroll.punchTypeClockOut");
    const lunch = t("pages.payroll.lunch");
    const netHours = t("pages.payroll.netHours");
    const dateTime = t("pages.patrolLogs.dateTime");
    const type = t("common.type");
    const location = t("pages.checkpoints.location");
    const nameLabel = t("common.name");
    const emailLabel = t("common.email");
    const paymentType = t("pages.payroll.paymentType");
    const hourlyRate = t("pages.payroll.hourlyRate");
    const from = t("pages.guardLoginRecords.from");
    const to = t("pages.guardLoginRecords.to");
    const employeeSignature = t("pages.payroll.employeeSignature");
    const authorizedSignature = t("pages.payroll.authorizedSignature");
    const documentGenerated = t("pages.payroll.documentGenerated");
    const systemTitle = t("login.systemTitle");
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${paystubTitle} - ${payrollDetail?.user_name || t("pages.payroll.employee")}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; line-height: 1.4; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .header p { color: #666; }
          .section { margin-bottom: 20px; }
          .section-title { background: #f5f5f5; padding: 8px 12px; font-weight: bold; border-left: 4px solid #333; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .total-section { background: #e8f5e9; padding: 15px; border-radius: 8px; text-align: center; margin-top: 20px; }
          .total-amount { font-size: 28px; font-weight: bold; color: #2e7d32; margin-top: 10px; }
          .signature-section { margin-top: 50px; display: flex; justify-content: space-between; }
          .signature-box { width: 45%; text-align: center; }
          .signature-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 10px; }
          .punch-type { padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold; }
          .clock_in { background: #c8e6c9; color: #2e7d32; }
          .clock_out { background: #ffcdd2; color: #c62828; }
          .lunch_out { background: #fff3e0; color: #ef6c00; }
          .lunch_in { background: #e3f2fd; color: #1565c0; }
          .footer { margin-top: 30px; text-align: center; color: #999; font-size: 10px; border-top: 1px solid #eee; padding-top: 10px; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${paystubTitle.toUpperCase()}</h1>
          <p>Sentinel Pro - ${systemTitle}</p>
        </div>
        <div class="section">
          <div class="section-title">${employeeInfo}</div>
          <table>
            <tr><td><strong>${nameLabel}:</strong></td><td>${payrollDetail?.user_name || 'N/A'}</td><td><strong>${emailLabel}:</strong></td><td>${payrollDetail?.email || 'N/A'}</td></tr>
            <tr><td><strong>${paymentType}:</strong></td><td>${payrollDetail?.payment_type === 'hourly' ? t("pages.users.hourly") : t("pages.users.mixed")}</td><td><strong>${hourlyRate}:</strong></td><td>$${payrollDetail?.hourly_rate?.toFixed(2) || '0.00'}</td></tr>
          </table>
        </div>
        <div class="section">
          <div class="section-title">${paymentPeriod}</div>
          <p><strong>${from}:</strong> ${payrollDetail?.period_start || 'N/A'} &nbsp;&nbsp; <strong>${to}:</strong> ${payrollDetail?.period_end || 'N/A'}</p>
        </div>
        <div class="section">
          <div class="section-title">${hoursSummary}</div>
          <table>
            <thead><tr><th>${dateLabel}</th><th>${clockIn}</th><th>${clockOut}</th><th>${lunch}</th><th>${netHours}</th></tr></thead>
            <tbody>
              ${payrollDetail?.workdays?.map(day => `<tr><td>${day.date}</td><td>${day.clock_in ? new Date(day.clock_in).toLocaleTimeString() : '-'}</td><td>${day.clock_out ? new Date(day.clock_out).toLocaleTimeString() : '-'}</td><td>${(day.lunch_hours || 0).toFixed(2)}h</td><td><strong>${(day.total_hours || 0).toFixed(2)}h</strong></td></tr>`).join('') || `<tr><td colspan="5">${noRecords}</td></tr>`}
              <tr style="background:#f5f5f5"><td colspan="3"></td><td><strong>${totalLunch}:</strong></td><td><strong>${payrollDetail?.lunch_hours?.toFixed(2) || '0.00'}h</strong></td></tr>
              <tr style="background:#e8f5e9"><td colspan="3"></td><td><strong>${totalHours}</strong></td><td><strong>${payrollDetail?.total_hours?.toFixed(2) || '0.00'}h</strong></td></tr>
            </tbody>
          </table>
        </div>
        <div class="section">
          <div class="section-title">${punchHistory}</div>
          <table>
            <thead><tr><th>${dateTime}</th><th>${type}</th><th>${location}</th></tr></thead>
            <tbody>
              ${payrollDetail?.time_records?.slice(0, 50).map(record => `<tr><td>${new Date(record.timestamp).toLocaleString()}</td><td><span class="punch-type ${record.punch_type}">${getPunchTypeLabel(record.punch_type)}</span></td><td style="font-size:10px;font-family:monospace">${record.latitude?.toFixed(4) || 'N/A'}, ${record.longitude?.toFixed(4) || 'N/A'}</td></tr>`).join('') || `<tr><td colspan="3">${noRecords}</td></tr>`}
            </tbody>
          </table>
        </div>
        <div class="total-section">
          <p>${payrollDetail?.payment_type === 'hourly' ? `${payrollDetail?.total_hours || 0}h × $${payrollDetail?.hourly_rate?.toFixed(2) || '0.00'}/h` : `${fixedSalary}: $${payrollDetail?.fixed_salary?.toFixed(2) || '0.00'} + ${payrollDetail?.total_hours || 0}h × $${payrollDetail?.hourly_rate?.toFixed(2) || '0.00'}/h`}</p>
          <p class="total-amount">${totalToPay} $${payrollDetail?.total_payment?.toFixed(2) || '0.00'}</p>
        </div>
        <div class="signature-section">
          <div class="signature-box"><div class="signature-line">${employeeSignature}</div><p style="font-size:11px;margin-top:5px">${dateLabel}: ____________</p></div>
          <div class="signature-box"><div class="signature-line">${authorizedSignature}</div><p style="font-size:11px;margin-top:5px">${dateLabel}: ____________</p></div>
        </div>
        <div class="footer">${documentGenerated} ${new Date().toLocaleString()} - Sentinel Pro</div>
      </body>
      </html>
    `;
    
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();
    
    // Wait for content to render, then trigger print
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 500);
  };

  // Filtrar usuarios sin actividad cuando hay filtros de fecha activos
  const hasDateFilter = startDate || endDate;
  const filteredPayrollData = Array.isArray(payrollData)
    ? (hasDateFilter
        ? payrollData.filter((p) => p.days_worked > 0 || p.total_hours > 0)
        : payrollData)
    : [];

  const totalPayroll = Array.isArray(filteredPayrollData) 
    ? filteredPayrollData.reduce((sum, p) => sum + (p.total_payment_after_advance || p.total_payment || 0), 0)
    : 0;
  const totalHours = Array.isArray(filteredPayrollData)
    ? filteredPayrollData.reduce((sum, p) => sum + (p.total_hours || 0), 0)
    : 0;
  const totalDays = Array.isArray(filteredPayrollData)
    ? filteredPayrollData.reduce((sum, p) => sum + (p.days_worked || 0), 0)
    : 0;

  const getPunchTypeLabel = (type) => {
    const labels = {
      clock_in: t("pages.payroll.punchTypeClockIn"),
      lunch_out: t("pages.payroll.punchTypeLunchOut"),
      lunch_in: t("pages.payroll.punchTypeLunchIn"),
      clock_out: t("pages.payroll.punchTypeClockOut"),
    };
    return labels[type] || type;
  };

  const getPunchTypeBadgeClass = (type) => {
    const classes = {
      clock_in: "bg-emerald-100 text-emerald-800 border-emerald-200",
      clock_out: "bg-red-100 text-red-800 border-red-200",
      lunch_out: "bg-amber-100 text-amber-800 border-amber-200",
      lunch_in: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return classes[type] || "bg-slate-100 text-slate-700";
  };

  const PaystubSection = ({ title, children }) => (
    <div className="rounded-lg border bg-white overflow-hidden shadow-sm">
      <div className="bg-slate-100 px-4 py-2.5 border-l-4 border-slate-800">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{t("nav.payroll")}</h1>
          <p className="text-slate-600 mt-1">{t("pages.payroll.subtitle")}</p>
        </div>
        <Button
          onClick={exportToExcel}
          className="bg-green-600 hover:bg-green-700"
          disabled={filteredPayrollData.length === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          {t("pages.payroll.exportToExcel")}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {canManageCompanies() && (
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">{t("pages.companies.name")}</Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger data-testid="filter-company">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.routes.allCompanies")}</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">Vigilante</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="filter-user">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los vigilantes</SelectItem>
                  {vigilantes.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.guardLoginRecords.from")}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.guardLoginRecords.to")}</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedCompany("");
                  setSelectedUser("");
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Limpiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("pages.payroll.totalPayroll")}</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${totalPayroll.toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Horas Totales</p>
                <p className="text-2xl font-bold text-slate-900">
                  {totalHours.toFixed(1)}h
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">{t("pages.payroll.workedDays")}</p>
                <p className="text-2xl font-bold text-slate-900">{totalDays}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <User className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Vigilantes</p>
                <p className="text-2xl font-bold text-slate-900">{Array.isArray(filteredPayrollData) ? filteredPayrollData.length : 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toggle para mostrar historial de pagos */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">{t("pages.payroll.summary")}</h2>
        <Button
          variant="outline"
          onClick={() => {
            setShowHistory(!showHistory);
            if (!showHistory) {
              fetchPaymentHistory();
            }
          }}
        >
          <History className="h-4 w-4 mr-2" />
          {showHistory ? "Ocultar" : "Mostrar"} Historial de Pagos
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Detalle por Vigilante
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vigilante</TableHead>
                <TableHead>Tipo Pago</TableHead>
                <TableHead className="text-right">{t("pages.payroll.hourlyRate")}</TableHead>
                <TableHead className="text-right">{t("pages.payroll.days")}</TableHead>
                <TableHead className="text-right">Horas</TableHead>
                <TableHead className="text-right">{t("pages.payroll.lunch")}</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-center">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : !Array.isArray(filteredPayrollData) || filteredPayrollData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    No hay datos de nómina para el período seleccionado
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayrollData.map((item) => (
                  <TableRow key={item.user_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.user_name}</p>
                        <p className="text-sm text-slate-500">{item.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          item.payment_type === "hourly"
                            ? "bg-blue-50 text-blue-700"
                            : "bg-purple-50 text-purple-700"
                        }
                      >
                        {item.payment_type === "hourly" ? "Por hora" : "Mixto"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${item.hourly_rate.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const totalDays = item.days_worked || 0;
                        const daysPaid = getDaysPaidForUser(item.user_id);
                        const hasPartialPayment = item.is_partial || (item.total_paid > 0 && item.total_paid < (item.total_payment_after_advance || item.total_payment));
                        const remainingDays = Math.max(0, totalDays - daysPaid);
                        
                        // Mostrar tachado y días restantes si hay pago parcial y se han pagado algunos días
                        if (hasPartialPayment && daysPaid > 0 && totalDays > 0) {
                          return (
                            <div className="flex flex-col items-end">
                              <span className="font-bold text-green-600">
                                {remainingDays}
                              </span>
                              <span className="text-xs text-slate-500 line-through">
                                {totalDays}
                              </span>
                            </div>
                          );
                        } else {
                          return <span>{totalDays}</span>;
                        }
                      })()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {item.total_hours.toFixed(2)}h
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-500">
                      {(item.lunch_hours || 0).toFixed(2)}h
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end">
                        {item.advance_debt > 0 && (
                          <span className="text-xs text-blue-600 mb-1">
                            Adelantado: ${item.advance_debt.toFixed(2)}
                          </span>
                        )}
                        {(() => {
                          const totalToPay = item.total_payment_after_advance || item.total_payment || 0;
                          const totalPaid = item.total_paid || 0;
                          const hasPartialPayment = totalPaid > 0 && totalPaid < totalToPay && !item.is_paid;
                          
                          if (hasPartialPayment || item.is_partial) {
                            return (
                              <>
                                <span className="font-bold text-green-600 font-mono">
                                  ${(totalToPay - totalPaid).toFixed(2)}
                                </span>
                                <span className="text-xs text-slate-500 line-through">
                                  ${totalToPay.toFixed(2)}
                                </span>
                                <span className="text-xs text-slate-400 mt-1">
                                  Pagado: ${totalPaid.toFixed(2)}
                                </span>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <span className="font-bold text-green-600 font-mono">
                                  ${totalToPay.toFixed(2)}
                                </span>
                                {item.advance_debt > 0 && (
                                  <span className="text-xs text-slate-500 line-through">
                                    ${item.total_payment.toFixed(2)}
                                  </span>
                                )}
                              </>
                            );
                          }
                        })()}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {item.is_paid ? (
                        <Badge className="bg-green-100 text-green-700 border-green-300">
                          <CheckCircle2 className="h-3 w-3 mr-1 inline" />
                          Pagado
                        </Badge>
                      ) : item.is_partial && item.total_paid > 0 ? (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                          <Clock className="h-3 w-3 mr-1 inline" />
                          Parcial (${item.total_paid.toFixed(2)})
                        </Badge>
                      ) : item.advance_debt > 0 ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                          <DollarSign className="h-3 w-3 mr-1 inline" />
                          Con Adelanto
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                          <XCircle className="h-3 w-3 mr-1 inline" />
                          Pendiente
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewPayrollDetail(item.user_id)}
                          data-testid={`view-detail-${item.user_id}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPaystub(item.user_id)}
                          data-testid={`generate-paystub-${item.user_id}`}
                          title="Generar volante"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewUserPaymentHistory(item.user_id)}
                          data-testid={`view-history-${item.user_id}`}
                          title="Ver historial de pagos"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        {can("payroll.manage") && (
                          <>
                            {!item.is_paid && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => openPaymentDialog(item)}
                                className={item.is_partial ? "bg-yellow-600 hover:bg-yellow-700" : "bg-green-600 hover:bg-green-700"}
                                data-testid={`mark-paid-${item.user_id}`}
                                title={item.is_partial ? "Agregar pago parcial" : "Saldar pago"}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openPaymentDialog(item, true)}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid={`advance-payment-${item.user_id}`}
                              title="Pago por adelantado"
                            >
                              <DollarSign className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Historial de Pagos */}
      {showHistory && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Historial de Pagos
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select
                  value={historyFilterUser}
                  onValueChange={(value) => {
                    setHistoryFilterUser(value);
                    fetchPaymentHistory(value !== "all" ? value : null);
                  }}
                >
                  <SelectTrigger className="w-48" data-testid="history-filter-user">
                    <SelectValue placeholder="Filtrar por usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los usuarios</SelectItem>
                    {Array.isArray(users) && users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : !Array.isArray(paymentHistory) || paymentHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>
                  {historyFilterUser !== "all" 
                    ? t("pages.payroll.noPaymentsForUser")
                    : t("pages.payroll.noPaymentsForPeriod")}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha de Pago</TableHead>
                    <TableHead>Vigilante</TableHead>
                    <TableHead>{t("pages.payroll.period")}</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead className="text-right">Horas</TableHead>
                    <TableHead className="text-right">{t("pages.payroll.days")}</TableHead>
                    <TableHead>Registrado por</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Notas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentHistory.map((payment) => {
                    const paidByUser = Array.isArray(users) ? users.find(u => u.id === payment.paid_by) : null;
                    const paymentUser = Array.isArray(users) ? users.find(u => u.id === payment.user_id) : null;
                    return (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {formatDateTime(payment.paid_at)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{paymentUser?.name || "Desconocido"}</p>
                            <p className="text-xs text-slate-500">{paymentUser?.email || ""}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{payment.period_start} - {payment.period_end}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-green-600">
                          ${payment.total_payment.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-slate-600">
                          {payment.total_hours.toFixed(2)}h
                        </TableCell>
                        <TableCell className="text-right text-slate-600">
                          {payment.days_worked}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{paidByUser?.name || "Desconocido"}</p>
                            <p className="text-xs text-slate-500">{paidByUser?.email || ""}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {payment.is_advance ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                              Por Adelantado
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                              Normal
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-600 max-w-xs truncate" title={payment.notes || ""}>
                            {payment.notes || "-"}
                          </p>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("pages.payroll.detail")} - {payrollDetail?.user_name}</DialogTitle>
          </DialogHeader>
          {payrollDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t("pages.payroll.period")}</p>
                  <p className="font-medium">
                    {payrollDetail.period_start || "N/A"} - {payrollDetail.period_end || "N/A"}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t("pages.payroll.workedHours")}</p>
                  <p className="font-medium">{payrollDetail.total_hours}h</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">{t("pages.payroll.totalToPay")}</p>
                  <p className="font-bold text-green-600">${payrollDetail.total_payment.toFixed(2)}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">{t("pages.payroll.workedDays")}</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Entrada</TableHead>
                      <TableHead>Salida</TableHead>
                      <TableHead>Almuerzo</TableHead>
                      <TableHead className="text-right">Horas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollDetail.workdays?.map((day) => (
                      <TableRow key={day.id}>
                        <TableCell>{day.date}</TableCell>
                        <TableCell>{day.clock_in ? new Date(day.clock_in).toLocaleTimeString() : "-"}</TableCell>
                        <TableCell>{day.clock_out ? new Date(day.clock_out).toLocaleTimeString() : "-"}</TableCell>
                        <TableCell>{day.lunch_hours?.toFixed(2) || 0}h</TableCell>
                        <TableCell className="text-right font-mono">
                          {day.total_hours?.toFixed(2) || 0}h
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Paystub Dialog */}
      <Dialog open={isPaystubOpen} onOpenChange={setIsPaystubOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b pl-6 pr-14 py-4 bg-white shrink-0">
            <div className="min-w-0 flex-1 pr-4">
              <DialogTitle className="text-lg font-semibold">
                {t("pages.payroll.paystubTitle")}
              </DialogTitle>
              {payrollDetail && (
                <p className="text-sm text-slate-500 mt-0.5 truncate">{payrollDetail.user_name}</p>
              )}
            </div>
            <Button onClick={handlePrint} size="sm" className="shrink-0">
              <Printer className="h-4 w-4 mr-2" />
              {t("common.print")}
            </Button>
          </div>

          <iframe
            ref={printIframeRef}
            style={{ display: "none" }}
            title="print-frame"
          />

          {payrollDetail && (
            <div ref={printRef} className="overflow-y-auto flex-1 px-6 py-5 space-y-5 bg-slate-50/60">
              <div className="text-center pb-4 border-b-2 border-slate-800">
                <h2 className="text-2xl font-bold tracking-wide text-slate-900 uppercase">
                  {t("pages.payroll.paystubTitle")}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Sentinel Pro — {t("login.systemTitle")}
                </p>
              </div>

              <PaystubSection title={t("pages.payroll.employeeInfo")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">{t("common.name")}</p>
                      <p className="font-medium text-slate-900">{payrollDetail.user_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">{t("common.email")}</p>
                      <p className="font-medium text-slate-900">{payrollDetail.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <DollarSign className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">{t("pages.payroll.paymentType")}</p>
                      <p className="font-medium text-slate-900">
                        {payrollDetail.payment_type === "hourly"
                          ? t("pages.users.hourly")
                          : t("pages.users.mixed")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-500">{t("pages.payroll.hourlyRate")}</p>
                      <p className="font-medium text-slate-900">
                        ${payrollDetail.hourly_rate.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </PaystubSection>

              <PaystubSection title={t("pages.payroll.paymentPeriod")}>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 border">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">{t("pages.guardLoginRecords.from")}:</span>
                    <span className="font-medium">{payrollDetail.period_start || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 border">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">{t("pages.guardLoginRecords.to")}:</span>
                    <span className="font-medium">{payrollDetail.period_end || "—"}</span>
                  </div>
                </div>
              </PaystubSection>

              <PaystubSection title={t("pages.payroll.hoursSummary")}>
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead>{t("common.date")}</TableHead>
                        <TableHead>{t("pages.payroll.punchTypeClockIn")}</TableHead>
                        <TableHead>{t("pages.payroll.punchTypeClockOut")}</TableHead>
                        <TableHead>{t("pages.payroll.lunch")}</TableHead>
                        <TableHead className="text-right">{t("pages.payroll.netHours")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payrollDetail.workdays?.length ? (
                        payrollDetail.workdays.map((day) => (
                          <TableRow key={day.id}>
                            <TableCell className="font-medium">{formatDate(day.date)}</TableCell>
                            <TableCell>
                              {day.clock_in ? formatTime(day.clock_in) : "—"}
                            </TableCell>
                            <TableCell>
                              {day.clock_out ? formatTime(day.clock_out) : "—"}
                            </TableCell>
                            <TableCell>{(day.lunch_hours || 0).toFixed(2)}h</TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              {(day.total_hours || 0).toFixed(2)}h
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-500 py-6">
                            {t("pages.payroll.noRecords")}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableCell colSpan={3} />
                        <TableCell className="font-semibold">{t("pages.payroll.totalLunch")}:</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {payrollDetail.lunch_hours.toFixed(2)}h
                        </TableCell>
                      </TableRow>
                      <TableRow className="bg-emerald-50 hover:bg-emerald-50">
                        <TableCell colSpan={3} />
                        <TableCell className="font-semibold text-emerald-800">
                          {t("pages.payroll.totalHours")}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-emerald-800">
                          {payrollDetail.total_hours.toFixed(2)}h
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </PaystubSection>

              <PaystubSection title={t("pages.payroll.punchHistory")}>
                <div className="rounded-md border overflow-hidden max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-slate-50">
                      <TableRow className="hover:bg-slate-50">
                        <TableHead>{t("pages.patrolLogs.dateTime")}</TableHead>
                        <TableHead>{t("common.type")}</TableHead>
                        <TableHead>{t("pages.checkpoints.location")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payrollDetail.time_records?.length ? (
                        payrollDetail.time_records.slice(0, 50).map((record) => (
                          <TableRow key={record.id}>
                            <TableCell className="text-sm whitespace-nowrap">
                              {formatDateTime(record.timestamp)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={cn("text-xs font-medium", getPunchTypeBadgeClass(record.punch_type))}
                              >
                                {getPunchTypeLabel(record.punch_type)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono text-slate-600">
                              {Number.isFinite(Number(record.latitude)) && Number.isFinite(Number(record.longitude)) ? (
                                <span className="inline-flex items-center gap-2">
                                  <span>
                                    {Number(record.latitude).toFixed(4)}, {Number(record.longitude).toFixed(4)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => openPunchMap(record)}
                                    className="inline-flex items-center justify-center rounded-md p-1 text-primary hover:bg-primary/10 transition-colors"
                                    title={t("pages.payroll.viewOnMap")}
                                    aria-label={t("pages.payroll.viewOnMap")}
                                    data-testid={`punch-map-${record.id}`}
                                  >
                                    <MapPin className="h-4 w-4" />
                                  </button>
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-500 py-6">
                            {t("pages.payroll.noRecords")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </PaystubSection>

              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-center shadow-sm">
                <p className="text-sm text-emerald-800">
                  {payrollDetail.payment_type === "hourly"
                    ? `${payrollDetail.total_hours.toFixed(2)}h × $${payrollDetail.hourly_rate.toFixed(2)}/h`
                    : `${t("pages.payroll.fixedSalary")}: $${payrollDetail.fixed_salary.toFixed(2)} + ${payrollDetail.total_hours.toFixed(2)}h × $${payrollDetail.hourly_rate.toFixed(2)}/h`}
                </p>
                <p className="text-3xl font-bold text-emerald-700 mt-2">
                  {t("pages.payroll.totalToPayLabel")} ${payrollDetail.total_payment.toFixed(2)}
                </p>
              </div>

              <Separator />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-2">
                <div className="text-center">
                  <div className="border-t border-slate-400 pt-2 mt-10">
                    <p className="text-sm font-medium text-slate-700">
                      {t("pages.payroll.employeeSignature")}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {t("common.date")}: ____________
                  </p>
                </div>
                <div className="text-center">
                  <div className="border-t border-slate-400 pt-2 mt-10">
                    <p className="text-sm font-medium text-slate-700">
                      {t("pages.payroll.authorizedSignature")}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {t("common.date")}: ____________
                  </p>
                </div>
              </div>

              <p className="text-center text-xs text-slate-400 pb-2">
                {t("pages.payroll.documentGenerated")} {new Date().toLocaleString()} — Sentinel Pro
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Pago Saldado</DialogTitle>
          </DialogHeader>
          {selectedPaymentItem && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm text-slate-600">Vigilante</p>
                <p className="font-medium">{selectedPaymentItem.user_name}</p>
                <p className="text-sm text-slate-500">{selectedPaymentItem.email}</p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-slate-600 mb-1 block">{t("pages.payroll.period")}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">{t("pages.guardLoginRecords.from")}</Label>
                      <Input
                        type="date"
                        value={paymentPeriod.start || startDate || ""}
                        onChange={(e) => setPaymentPeriod({ ...paymentPeriod, start: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">{t("pages.guardLoginRecords.to")}</Label>
                      <Input
                        type="date"
                        value={paymentPeriod.end || endDate || ""}
                        onChange={(e) => setPaymentPeriod({ ...paymentPeriod, end: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  {isAdvancePayment && (
                    <p className="text-xs text-blue-600 mt-1">
                      Para pagos por adelantado, puede establecer cualquier período futuro
                    </p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm text-slate-600 mb-1 block">
                    Monto a Pagar
                    <span className="text-xs text-slate-400 ml-2">
                      {selectedPaymentItem.advance_debt > 0 ? (
                        <>
                          (Total: ${selectedPaymentItem.total_payment.toFixed(2)} - Adelanto: ${selectedPaymentItem.advance_debt.toFixed(2)} = ${(selectedPaymentItem.total_payment_after_advance || selectedPaymentItem.total_payment).toFixed(2)})
                        </>
                      ) : (
                        <>
                          (Total calculado: ${selectedPaymentItem.total_payment.toFixed(2)})
                        </>
                      )}
                      {selectedPaymentItem.is_partial && selectedPaymentItem.total_paid > 0 && (
                        <span className="text-yellow-600 ml-2">
                          • Ya pagado: ${selectedPaymentItem.total_paid.toFixed(2)}
                        </span>
                      )}
                    </span>
                  </Label>
                  {(() => {
                    const totalToPay = selectedPaymentItem.total_payment_after_advance || selectedPaymentItem.total_payment || 0;
                    const totalPaid = selectedPaymentItem.total_paid || 0;
                    const remainingBalance = totalToPay - totalPaid;
                    const maxAmount = isAdvancePayment ? undefined : remainingBalance;
                    
                    return (
                      <>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                          max={maxAmount}
                    value={paymentAmount}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            // Si no es adelantado, limitar al saldo pendiente
                            if (!isAdvancePayment && value > remainingBalance) {
                              setPaymentAmount(remainingBalance);
                              toast.warning(`El monto máximo permitido es $${remainingBalance.toFixed(2)}`);
                            } else {
                              setPaymentAmount(value);
                            }
                          }}
                          className={`font-mono ${!isAdvancePayment && paymentAmount > remainingBalance ? 'border-red-500' : ''}`}
                    placeholder="0.00"
                  />
                        {!isAdvancePayment && (
                          <p className="text-xs text-slate-600 mt-1">
                            Saldo pendiente: <span className="font-semibold text-green-600">${remainingBalance.toFixed(2)}</span>
                          </p>
                        )}
                  {selectedPaymentItem.advance_debt > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      {t("pages.payroll.advanceDebt", { amount: selectedPaymentItem.advance_debt.toFixed(2) })}
                    </p>
                  )}
                        {!isAdvancePayment && paymentAmount > remainingBalance && (
                          <p className="text-xs text-red-600 mt-1 font-semibold">
                            ⚠️ {t("api.errors.cannotPayMoreThan", { amount: remainingBalance.toFixed(2) })}
                    </p>
                  )}
                        {!isAdvancePayment && paymentAmount > 0 && paymentAmount < remainingBalance && (
                    <p className="text-xs text-orange-600 mt-1">
                            {t("pages.payroll.partialPayment", { amount: (remainingBalance - paymentAmount).toFixed(2) })}
                    </p>
                  )}
                      </>
                    );
                  })()}
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-600 mb-1 block">{t("pages.payroll.notesOptional")}</Label>
                <Textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder={t("pages.payroll.addNotesPlaceholder")}
                  rows={3}
                />
              </div>

              <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Checkbox
                  id="isAdvancePayment"
                  checked={isAdvancePayment}
                  onCheckedChange={(checked) => setIsAdvancePayment(checked === true)}
                />
                <Label htmlFor="isAdvancePayment" className="text-sm text-slate-700 cursor-pointer flex-1">
                  <span className="font-medium">Pago por adelantado</span>
                  <span className="text-xs text-slate-500 block mt-1">
                    El usuario aún no ha trabajado las horas correspondientes. Este pago se registrará con 0 horas y 0 días trabajados.
                  </span>
                </Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsPaymentDialogOpen(false);
                    setSelectedPaymentItem(null);
                    setPaymentNotes("");
                    setPaymentAmount(0);
                    setPaymentPeriod({ start: "", end: "" });
                    setIsAdvancePayment(false);
                  }}
                >
                  {t("common.cancel")}
                </Button>
                {(() => {
                  const totalToPay = selectedPaymentItem.total_payment_after_advance || selectedPaymentItem.total_payment || 0;
                  const totalPaid = selectedPaymentItem.total_paid || 0;
                  const remainingBalance = totalToPay - totalPaid;
                  const exceedsBalance = !isAdvancePayment && paymentAmount > remainingBalance;
                  
                  return (
                <Button
                  onClick={markAsPaid}
                  className="bg-green-600 hover:bg-green-700"
                      disabled={!paymentPeriod.start || !paymentPeriod.end || paymentAmount <= 0 || exceedsBalance}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirmar Pago
                </Button>
                  );
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mapa de la ubicación de un punch (desde el volante de pago) */}
      <LocationMapDialog
        open={isMapDialogOpen}
        onOpenChange={setIsMapDialogOpen}
        location={mapLocation}
        title={mapTitle}
      />
    </div>
  );
};
